import { useEffect, useRef, type RefObject } from 'react';

const layers: HTMLElement[] = [];
const inertBefore = new Map<HTMLElement, boolean>();
let bodyWasLocked = false;
const focusable = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

function syncBackground() {
  for (const [element, inert] of inertBefore) element.inert = inert;
  inertBefore.clear();
  let branch = layers.at(-1);
  while (branch?.parentElement) {
    for (const sibling of branch.parentElement.children) {
      if (sibling instanceof HTMLElement && sibling !== branch && !sibling.hasAttribute('data-modal-backdrop')) {
        inertBefore.set(sibling, sibling.inert);
        sibling.inert = true;
      }
    }
    branch = branch.parentElement;
    if (branch === document.body) break;
  }
}

/** The topmost modal owns focus and makes all other DOM branches inert. */
export function useModalFocus(ref: RefObject<HTMLElement | null>, active: boolean, onClose: () => void) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!active || !dialog) return;
    const previous = document.activeElement as HTMLElement | null;
    if (!layers.length) bodyWasLocked = document.body.classList.contains('modal-open');
    layers.push(dialog);
    document.body.classList.add('modal-open');
    syncBackground();
    const frame = requestAnimationFrame(() => dialog.focus());
    const isTop = () => layers.at(-1) === dialog;
    const onKey = (event: KeyboardEvent) => {
      if (!isTop()) return;
      if (event.key === 'Escape') {
        // Inline confirmations own Escape without creating a second modal.
        if (event.target instanceof Element && event.target.closest('[data-modal-escape-boundary]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        close.current();
      }
      if (event.key !== 'Tab') return;
      const items = [...dialog.querySelectorAll<HTMLElement>(focusable)].filter(element =>
        element.tabIndex >= 0 && !element.closest('[inert], [hidden]') && getComputedStyle(element).visibility !== 'hidden' && getComputedStyle(element).display !== 'none');
      const first = items[0];
      const last = items.at(-1);
      if (!first) { event.preventDefault(); dialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {
        event.preventDefault(); first.focus();
      }
    };
    const onFocus = (event: FocusEvent) => {
      if (isTop() && !dialog.contains(event.target as Node)) dialog.focus();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocus);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocus);
      layers.splice(layers.indexOf(dialog), 1);
      syncBackground();
      if (!layers.length && !bodyWasLocked) document.body.classList.remove('modal-open');
      if (previous?.isConnected && !previous.closest('[inert]')) previous.focus();
    };
  }, [active, ref]);
}
